import { Check, Copy, Lock, QrCode, Share2, X } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useMemo, useRef, useState } from "react";
import { ModalDialog } from "../../components/ModalDialog";
import { roomInviteURL } from "../../lib/navigation";
import { meetingDetails } from "../../lib/scheduling";
import type { TalkRoom } from "../../lib/types";
import { ui } from "../../lib/ui";

function placeWord(count: number): string {
  const mod10 = Math.abs(count) % 10;
  const mod100 = Math.abs(count) % 100;
  if (mod10 === 1 && mod100 !== 11) return "места";
  return "мест";
}

export function InvitationDialog({ room, code, participantCount, onClose }: { room: TalkRoom; code: string; participantCount: number; onClose: () => void }) {
  const url = useMemo(() => roomInviteURL(code, location.origin), [code]);
  const details = useMemo(() => meetingDetails(room, url), [room, url]);
  const [qrURL, setQRURL] = useState("");
  const [qrFailed, setQRFailed] = useState(false);
  const [status, setStatus] = useState("");
  const statusTimer = useRef<number | undefined>(undefined);
  const canShare = typeof navigator.share === "function";

  useEffect(() => {
    let active = true;
    QRCode.toDataURL(url, { width: 240, margin: 1, errorCorrectionLevel: "M" }).then((value) => {
      if (active) setQRURL(value);
    }).catch(() => {
      if (active) setQRFailed(true);
    });
    return () => { active = false; };
  }, [url]);

  useEffect(() => () => {
    if (statusTimer.current !== undefined) window.clearTimeout(statusTimer.current);
  }, []);

  function announce(value: string) {
    if (statusTimer.current !== undefined) window.clearTimeout(statusTimer.current);
    setStatus(value);
    statusTimer.current = window.setTimeout(() => setStatus(""), 3_000);
  }

  async function copy(value: string, label: string) {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const field = document.createElement("textarea");
        field.value = value;
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.append(field);
        field.select();
        const copied = document.execCommand("copy");
        field.remove();
        if (!copied) throw new Error("copy unavailable");
      }
      announce(label);
    } catch {
      announce(ui("Copy failed. Select the meeting link and copy it manually.", "Не удалось скопировать. Выделите ссылку и скопируйте её вручную."));
    }
  }

  async function share() {
    try {
      await navigator.share({ title: room.title, text: details.replace(`${ui("Join", "Ссылка")}: ${url}\n`, ""), url });
      announce(ui("Share sheet opened", "Открыто меню «Поделиться»"));
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) announce(ui("Sharing was not available", "Поделиться не удалось"));
    }
  }

  return (
    <ModalDialog labelledBy="invite-title" className="invite-dialog invite-dialog--complete" onClose={onClose}>
      <header><div><h2 id="invite-title">{ui("Invite people", "Пригласить людей")}</h2><p>{ui("One reusable link, ready for chat, mail, or another device.", "Одна постоянная ссылка — для чата, почты или другого устройства.")}</p></div><button type="button" aria-label={ui("Close invitation", "Закрыть приглашение")} onClick={onClose}><X aria-hidden="true" /></button></header>
      <div className="invite-complete-grid">
        <div>
          <div className="invite-code"><span>{ui("Room code", "Код комнаты")}</span><strong>{code}</strong><button type="button" onClick={() => void copy(code, ui("Room code copied", "Код комнаты скопирован"))}>{ui("Copy code", "Копировать код")}</button></div>
          <label className="invite-link"><span>{ui("Meeting link", "Ссылка на комнату")}</span><input readOnly value={url} onFocus={(event) => event.currentTarget.select()} /></label>
          <div className={`invite-actions${canShare ? "" : " invite-actions--copy-only"}`}>
            {canShare ? <button className="button button--primary" type="button" onClick={() => void share()}><Share2 aria-hidden="true" />{ui("Share invite", "Поделиться")}</button> : null}
            <button className="button button--secondary" type="button" onClick={() => void copy(url, ui("Room link copied", "Ссылка на комнату скопирована"))}><Copy aria-hidden="true" />{ui("Copy link", "Копировать ссылку")}</button>
            <button className="button button--secondary" type="button" onClick={() => void copy(details, ui("Meeting details copied", "Сведения скопированы"))}><Check aria-hidden="true" />{ui("Copy meeting details", "Копировать сведения")}</button>
          </div>
        </div>
        <div className={`invite-qr${qrFailed ? " is-unavailable" : ""}`}>{qrURL ? <img src={qrURL} width="168" height="168" alt={ui("QR code for the reusable meeting link", "QR-код постоянной ссылки на комнату")} /> : <QrCode aria-hidden="true" />}<span>{qrFailed ? ui("QR unavailable", "QR-код недоступен") : ui("Scan to join", "Сканируйте, чтобы войти")}</span></div>
      </div>
      <p className="invite-policy"><Lock aria-hidden="true" size={16} />{room.state === "locked" ? ui("This room is currently locked.", "Сейчас комната закрыта.") : ui(`${participantCount} of ${room.participantLimit} places used.`, `Занято ${participantCount} из ${room.participantLimit} ${placeWord(room.participantLimit)}.`)}</p>
      <span className="invite-copy-status" role="status" aria-live="polite">{status}</span>
    </ModalDialog>
  );
}
